import { FC } from "react"

import {
  Box,
  Checkbox,
  CheckboxProps,
  TableSortLabel,
  TableSortLabelProps,
} from "@mui/material"
import BasicTable from "components/common/table/basicTable"
import { useAppSelector } from "hooks/store"
import { useTranslation } from "react-i18next"
import { selectDisplaySavings } from "store/displayMode/displayMode.selectors"
import { IObjectStudentDetailed } from "ts/interfaces/Student"
import { createCheckboxClickHandler } from "utils/checkbox"

import { Sort } from "../TableView"

interface IProps {
  checkboxProps: CheckboxProps
  getTableSortLabelProps: (
    property: keyof IObjectStudentDetailed
  ) => TableSortLabelProps
  savingsAccountOption?: boolean
  sortBy?: Sort
  hideGroupColumn?: boolean
  children?: React.ReactNode
}

const Table: FC<IProps> = ({
  checkboxProps,
  getTableSortLabelProps,
  savingsAccountOption,
  sortBy,
  hideGroupColumn,
  children,
}) => {
  const { t } = useTranslation()
  const switchState = useAppSelector(selectDisplaySavings)

  const getStudentColumnSuffix = () => {
    if (sortBy === "first_name") {
      return ` (${t("students.firstName")})`
    }
    if (sortBy === "last_name") {
      return ` (${t("students.lastName")})`
    }
    return ""
  }

  return (
    <BasicTable
      wrapperProps={{
        borderRadius: "0px",
        sx: { border: "none" },
      }}
      head={
        <BasicTable.Row>
          <BasicTable.Cell 
            width="50px" 
            px="8px"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              py="2px"
              onClick={createCheckboxClickHandler(checkboxProps)}
              sx={{ 
                cursor: "pointer",
                width: "fit-content",
                height: "fit-content",
                margin: 0,
                padding: 0,
                "& .MuiCheckbox-root": {
                  margin: 0,
                },
              }}
            >
              <Checkbox {...checkboxProps} />
            </Box>
          </BasicTable.Cell>
          <BasicTable.Cell
            minWidth="100px"
            tableCellProps={{
              width: "50%",
              sortDirection:
                sortBy === "first_name" || sortBy === "last_name"
                  ? getTableSortLabelProps("first_name").direction
                  : false,
            }}
          >
            <TableSortLabel active={sortBy === "first_name" || sortBy === "last_name"} {...getTableSortLabelProps("first_name")}>
              <BasicTable.HeadText textTransform="uppercase">
                {t("students.student")}
                {getStudentColumnSuffix()}
              </BasicTable.HeadText>
            </TableSortLabel>
          </BasicTable.Cell>
          {!hideGroupColumn && (
            <BasicTable.Cell minWidth="100px" tableCellProps={{ width: "25%" }}>
              <BasicTable.HeadText textTransform="uppercase">
                {t("group.groupColumn")}
              </BasicTable.HeadText>
            </BasicTable.Cell>
          )}
          <BasicTable.Cell minWidth="100px" tableCellProps={{ width: "25%" }}>
            <BasicTable.HeadText textTransform="uppercase">
              {t("students.job")}
            </BasicTable.HeadText>
          </BasicTable.Cell>
          <BasicTable.Cell
            width="90px"
            tableCellProps={{
              sortDirection:
                sortBy === "balance"
                  ? getTableSortLabelProps("balance").direction
                  : false,
            }}
          >
            <TableSortLabel active={sortBy === "balance"} {...getTableSortLabelProps("balance")}>
              <BasicTable.HeadText textTransform="uppercase">
                {t("students.balance")}
              </BasicTable.HeadText>
            </TableSortLabel>
          </BasicTable.Cell>
          <BasicTable.Cell
            width="90px"
            tableCellProps={{
              sortDirection:
                sortBy === "pending_balance"
                  ? getTableSortLabelProps("pending_balance").direction
                  : false,
            }}
          >
            <TableSortLabel active={sortBy === "pending_balance"} {...getTableSortLabelProps("pending_balance")}>
              <BasicTable.HeadText textTransform="uppercase">
                {t("students.pending")}
              </BasicTable.HeadText>
            </TableSortLabel>
          </BasicTable.Cell>
          {savingsAccountOption && switchState && (
            <>
              <BasicTable.Cell width="90px">
                <TableSortLabel active={sortBy === "balance"} {...getTableSortLabelProps("balance")}>
                  <BasicTable.HeadText textTransform="uppercase">
                    {t("students.checking")}
                  </BasicTable.HeadText>
                </TableSortLabel>
              </BasicTable.Cell>
              <BasicTable.Cell width="90px">
                <TableSortLabel active={sortBy === "balance"} {...getTableSortLabelProps("balance")}>
                  <BasicTable.HeadText textTransform="uppercase">
                    {t("students.savings")}
                  </BasicTable.HeadText>
                </TableSortLabel>
              </BasicTable.Cell>
            </>
          )}
          <BasicTable.Cell width="109px" />
        </BasicTable.Row>
      }
      body={children}
    />
  )
}

export default Table
